/**
 * Enhanced error reporting utilities with global error handling
 */

interface ErrorReport {
  message: string;
  stack?: string;
  componentStack?: string;
  url: string;
  userAgent: string;
  timestamp: string;
  userId?: string;
  sessionId: string;
  errorType: 'javascript' | 'promise' | 'react' | 'api';
}

class ErrorReportingService {
  private sessionId: string;
  private isInitialized = false;
  private errorQueue: ErrorReport[] = [];
  private isOnline = navigator.onLine;

  constructor() {
    this.sessionId = this.generateSessionId();
    this.setupOnlineListener();
  }

  /**
   * Initialize global error handlers
   */
  initialize() {
    if (this.isInitialized) return;

    // Global JavaScript error handler
    window.onerror = (message, source, lineno, colno, error) => {
      this.reportError({
        message: typeof message === 'string' ? message : 'Unknown error',
        stack: error?.stack,
        url: window.location.href,
        userAgent: navigator.userAgent,
        timestamp: new Date().toISOString(),
        sessionId: this.sessionId,
        errorType: 'javascript'
      });
      return false; // Don't prevent default error handling
    };

    // Unhandled promise rejection handler
    window.addEventListener('unhandledrejection', (event) => {
      const error = event.reason;
      this.reportError({
        message: error?.message || 'Unhandled promise rejection',
        stack: error?.stack,
        url: window.location.href,
        userAgent: navigator.userAgent,
        timestamp: new Date().toISOString(),
        sessionId: this.sessionId,
        errorType: 'promise'
      });
    });

    this.isInitialized = true;
  }

  /**
   * Report an error (used by ErrorBoundary and manual reporting)
   */
  async reportError(errorData: Partial<ErrorReport>) {
    const report: ErrorReport = {
      message: errorData.message || 'Unknown error',
      stack: errorData.stack,
      componentStack: errorData.componentStack,
      url: errorData.url || window.location.href,
      userAgent: errorData.userAgent || navigator.userAgent,
      timestamp: errorData.timestamp || new Date().toISOString(),
      sessionId: this.sessionId,
      errorType: errorData.errorType || 'react',
      userId: this.getUserId()
    };

    // Add to queue for offline handling
    this.errorQueue.push(report);

    // Try to send immediately if online
    if (this.isOnline) {
      await this.flushErrorQueue();
    }

    // Log to console in development
    if (process.env.NODE_ENV === 'development') {
      console.group('🚨 Error Report');
      console.error('Message:', report.message);
      console.error('Stack:', report.stack);
      if (report.componentStack) {
        console.error('Component Stack:', report.componentStack);
      }
      console.error('Full Report:', report);
      console.groupEnd();
    }
  }

  /**
   * Flush queued errors to backend
   */
  private async flushErrorQueue() {
    if (this.errorQueue.length === 0) return;

    const errors = [...this.errorQueue];
    this.errorQueue = [];

    try {
      const response = await fetch('/api/errors/batch', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token') || ''}`
        },
        body: JSON.stringify({ errors })
      });

      if (!response.ok) {
        // Re-queue errors if send failed
        this.errorQueue.unshift(...errors);
      }
    } catch (error) {
      // Re-queue errors if send failed
      this.errorQueue.unshift(...errors);
      console.warn('Failed to send error reports:', error);
    }
  }

  /**
   * Setup online/offline listeners
   */
  private setupOnlineListener() {
    window.addEventListener('online', () => {
      this.isOnline = true;
      this.flushErrorQueue();
    });

    window.addEventListener('offline', () => {
      this.isOnline = false;
    });
  }

  /**
   * Generate unique session ID
   */
  private generateSessionId(): string {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Get current user ID from localStorage
   */
  private getUserId(): string | undefined {
    try {
      const token = localStorage.getItem('token');
      if (token) {
        const payload = JSON.parse(atob(token.split('.')[1]));
        return payload.userId;
      }
    } catch (error) {
      // Ignore token parsing errors
    }
    return undefined;
  }

  /**
   * Get error statistics for monitoring
   */
  getErrorStats() {
    return {
      queuedErrors: this.errorQueue.length,
      sessionId: this.sessionId,
      isOnline: this.isOnline,
      isInitialized: this.isInitialized
    };
  }

  /**
   * Clear error queue (for testing)
   */
  clearQueue() {
    this.errorQueue = [];
  }
}

// Export singleton instance
export const errorReportingService = new ErrorReportingService();

// Convenience function for manual error reporting
export const reportError = (error: Error, context?: string) => {
  errorReportingService.reportError({
    message: error.message,
    stack: error.stack,
    errorType: 'javascript',
    componentStack: context
  });
};

// Recovery utilities
export const withErrorRecovery = <T extends any[], R>(
  fn: (...args: T) => R,
  fallback: R,
  context?: string
) => {
  return (...args: T): R => {
    try {
      return fn(...args);
    } catch (error) {
      reportError(error as Error, context);
      return fallback;
    }
  };
};

export const withAsyncErrorRecovery = <T extends any[], R>(
  fn: (...args: T) => Promise<R>,
  fallback: R,
  context?: string
) => {
  return async (...args: T): Promise<R> => {
    try {
      return await fn(...args);
    } catch (error) {
      reportError(error as Error, context);
      return fallback;
    }
  };
};